/* eslint-disable import/no-unused-modules */
import AltRouteIcon from '@mui/icons-material/AltRoute'
import ApartmentIcon from '@mui/icons-material/Apartment'
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined'
import GroupIcon from '@mui/icons-material/Group'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import { Box, Button, Grid } from '@mui/material'
import DisplayPrice from 'components/Common/DisplayValues/DisplayPrice'
import hoursMinutesDisplay from 'components/Common/DisplayValues/hoursMinuteDisplay'
import milesDisplay from 'components/Common/DisplayValues/milesDisplay'
import moment from 'moment'
import React from 'react'
import { connect } from 'react-redux'
import { useHistory } from 'react-router-dom'
import DirectionsMap from '../../Common/DirectionsMap/DirectionsMap'
import BookingSummaryEntry from './BookingSummaryEntry'
import styles from './bookingSummaryStyles'

export function BookingSummary({
  bookingPreview,
  formId = undefined,
  isLoadingBooking = false,
  getTempBookedCars = [],
  navButtons = true,
  onSubmit,
  onPrecheckSubmit,
  onRouteInfoChange,
}) {
  const history = useHistory()
  const [previewMileage, setPreviewMileage] = React.useState(
    bookingPreview?.estimatedJourneyDistance ?? null,
  )
  const [previewETA, setPreviewETA] = React.useState(
    bookingPreview?.estimatedJourneyTime ?? null,
  )
  const [totalVehicleCount, setTotalVehicleCount] = React.useState(0)

  React.useEffect(() => {
    setPreviewMileage(bookingPreview?.estimatedJourneyDistance ?? null)
    setPreviewETA(bookingPreview?.estimatedJourneyTime ?? null)
  }, [
    bookingPreview?.estimatedJourneyDistance,
    bookingPreview?.estimatedJourneyTime,
  ])

  const updateRouteInfo = React.useCallback(
    (routeInfo) => {
      if (!routeInfo) {
        return
      }

      if (
        previewETA !== routeInfo.estimatedJourneyTime ||
        previewMileage !== routeInfo.estimatedJourneyDistance
      ) {
        setPreviewMileage(routeInfo?.estimatedJourneyDistance)
        setPreviewETA(routeInfo?.estimatedJourneyTime)
        onRouteInfoChange?.(routeInfo)
      }
    },
    [onRouteInfoChange, previewETA, previewMileage],
  )

  const getLeadPassenger = (preview) => {
    let leadPassenger
    if (preview) {
      leadPassenger = preview?.passengers?.filter(
        (passenger) => passenger?.isLeadPassenger,
      )
    }
    return leadPassenger?.length > 0 ? leadPassenger[0]?.name : ''
  }
  React.useEffect(() => {
    if (getTempBookedCars) {
      const totalOutboundVehicles = getTempBookedCars?.outBound?.reduce(
        (acc, curr) => {
          return (acc += curr.vehicleData.quantity)
        },
        0,
      )
      const totalInboundVehicles = getTempBookedCars?.inBound?.reduce(
        (acc, curr) => {
          return (acc += curr.vehicleData.quantity)
        },
        0,
      )
      setTotalVehicleCount(totalOutboundVehicles + totalInboundVehicles)
    }
  }, [getTempBookedCars])

  if (!bookingPreview) {
    return null
  }

  const filteredAdditionalStops = (bookingPreview.additionalStops || []).filter(
    (stop) => {
      const latitude = Number(stop?.latitude ?? stop?.lat)
      const longitude = Number(stop?.longitude ?? stop?.lng)

      return !(latitude === 0 && longitude === 0)
    },
  )

  return (
    <Box component={'div'} sx={styles.rootBackground}>
      <Grid container style={{ width: '100%' }}>
        {bookingPreview ? (
          <Grid item xs={12}>
            <DirectionsMap
              origin={bookingPreview.pickup}
              destination={bookingPreview.dropOff}
              additionalStops={filteredAdditionalStops}
              mapWidth="100%"
              mapHeight="250px"
              borderRadius="8px 8px 0 0"
              updateRouteInfo={updateRouteInfo}
            />
          </Grid>
        ) : null}
        <Box
          component="div"
          sx={(theme) => ({
            backgroundColor: theme.components.bookingSummary.headerColour,
            width: '100%',
            paddingTop: '25px',
            paddingBottom: '10px',
            paddingLeft: '25px',
          })}
        >
          <Box
            component={'p'}
            sx={(theme) => ({
              fontFamily: 'Montserrat',
              fontSize: '19px',
              fontWeight: 'regular',
              color: theme.components.bookingSummary.headerTextColour || '#fff',
              textTransform: 'uppercase',
            })}
          >
            Journey Plan
          </Box>
        </Box>

        <Grid item xs={12} sx={styles.containerDiv}>
          <Box component="div">
            <BookingSummaryEntry
              Icon={ApartmentIcon}
              title="client"
              value={bookingPreview?.companyName && bookingPreview.companyName}
            />
            <BookingSummaryEntry
              Icon={GroupIcon}
              title="passenger"
              value={getLeadPassenger(bookingPreview)}
            />
            <BookingSummaryEntry
              Icon={CalendarTodayOutlinedIcon}
              title="date"
              value={
                bookingPreview?.isImmediateJourney
                  ? 'ASAP'
                  : bookingPreview?.when &&
                    moment(bookingPreview.when).format('DD MMMM YYYY, h:mm a')
              }
            />
            <BookingSummaryEntry
              Icon={LocationOnIcon}
              title="from"
              value={
                bookingPreview?.pickup && (
                  <Box component={'p'} sx={styles.summaryValue}>
                    {bookingPreview.pickup.formattedAddress}
                  </Box>
                )
              }
            />
            <BookingSummaryEntry
              Icon={null}
              title="to"
              value={
                bookingPreview?.dropOff && (
                  <Box component={'p'} sx={styles.summaryValue}>
                    {bookingPreview.dropOff.formattedAddress}
                  </Box>
                )
              }
            />
            <BookingSummaryEntry
              Icon={AltRouteIcon}
              title="Estimated Route"
              value={
                previewMileage && (
                  <Box component={'p'} sx={styles.summaryValue}>
                    {milesDisplay({ miles: previewMileage })},{' '}
                    {hoursMinutesDisplay({ minutes: previewETA })}
                  </Box>
                )
              }
            />
          </Box>
          <Box
            component="div"
            sx={(theme) => ({
              padding: '10px',
              backgroundColor:
                theme.components.bookingSummary.backgroundImagePrice,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            })}
          >
            <Box
              component="p"
              sx={(theme) => ({
                textTransform: 'uppercase',
                fontFamily: 'Inter',
                fontSize: '11px',
                color: theme.components.bookingSummary.sectionTitle,
              })}
            >
              {bookingPreview.fixedPrice ? 'Fixed Price' : 'Estimated Price'}
            </Box>
            <Box
              component="p"
              sx={(theme) => ({
                textTransform: 'uppercase',
                fontFamily: 'Inter',
                fontSize: '36px',
                fontWeight: 'bold',
                color:
                  bookingPreview?.estimatedPrice ||
                  bookingPreview?.estimatedPriceFull
                    ? theme.components.bookingSummary.priceValue ??
                      theme.palette.white.main
                    : theme.components.bookingSummary.sectionAnswerPending,
              })}
            >
              {DisplayPrice({
                VATTotalPrice: bookingPreview?.estimatedPriceFull,
                priceType: bookingPreview.fixedPrice ? 'exact' : 'range',
                displayVATPrice: bookingPreview.displayVATPrice,
              })}
            </Box>
          </Box>

          {navButtons ? (
            <div>
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '15px',
                  textTransform: 'uppercase',
                  fontFamily: 'Inter',
                  fontSize: '20px',
                  color: '#ffffff',
                }}
              >
                <Button outlined={1} onClick={() => history.goBack()}>
                  back
                </Button>
                <Button
                  type="submit"
                  form={formId}
                  disabled={
                    isLoadingBooking ||
                    !bookingPreview?.companyName ||
                    (history.location.pathname.includes('/create/transport') &&
                      totalVehicleCount === 0)
                  }
                  onClick={onPrecheckSubmit || onSubmit}
                >
                  next
                </Button>
              </Box>
            </div>
          ) : (
            <></>
          )}
        </Grid>
      </Grid>
    </Box>
  )
}

const mapStateToProps = (state) => ({
  currentUser: state.users.user,
  currentClient: state.clients.client,
})

export default connect(mapStateToProps)(BookingSummary)
